import { useEffect, useRef, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  opacitySpeed: number;
  color: string;
}

const COLORS = [
  'rgba(200, 160, 80,',
  'rgba(220, 180, 100,',
  'rgba(180, 140, 70,',
  'rgba(240, 200, 120,',
  'rgba(160, 120, 60,',
];

function isMobile(): boolean {
  return window.innerWidth < 768;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface ParticleBackgroundProps {
  compact?: boolean;
}

export default function ParticleBackground({ compact = false }: ParticleBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const isMobileRef = useRef(isMobile());
  const reducedMotionRef = useRef(prefersReducedMotion());
  const compactRef = useRef(compact);
  compactRef.current = compact;

  const initParticles = useCallback((width: number, height: number) => {
    if (reducedMotionRef.current) {
      particlesRef.current = [];
      return;
    }

    const mobile = isMobileRef.current;
    const isCompact = compactRef.current;
    // Desktop full: up to 80. Desktop compact: up to 40. Mobile: up to 12.
    const count = mobile
      ? Math.min(12, Math.floor((width * height) / 60000))
      : Math.min(isCompact ? 40 : 80, Math.floor((width * height) / (isCompact ? 22000 : 12000)));

    const baseSpeed = mobile ? 0.12 : (isCompact ? 0.22 : 0.3);
    const baseSize = mobile ? 2.0 : (isCompact ? 2.0 : 2.5);
    const minSize = mobile ? 1.0 : (isCompact ? 0.8 : 0.8);

    const particles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * baseSpeed,
        vy: (Math.random() - 0.5) * baseSpeed - baseSpeed * 0.5,
        size: Math.random() * baseSize + minSize,
        opacity: Math.random() * 0.5 + 0.08,
        opacitySpeed: (Math.random() - 0.5) * (mobile ? 0.0025 : 0.004),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      });
    }
    particlesRef.current = particles;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      isMobileRef.current = isMobile();
      reducedMotionRef.current = prefersReducedMotion();
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles(canvas.width, canvas.height);
    };

    resize();
    window.addEventListener('resize', resize);

    // Only track mouse on desktop — mobile has no persistent cursor
    const handleMouse = (e: MouseEvent) => {
      if (!isMobileRef.current) {
        mouseRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    window.addEventListener('mousemove', handleMouse);

    if (reducedMotionRef.current) {
      if (ctx && canvas) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return () => {
        window.removeEventListener('resize', resize);
        window.removeEventListener('mousemove', handleMouse);
      };
    }

    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const particles = particlesRef.current;
      const mouse = mouseRef.current;
      const mobile = isMobileRef.current;
      const isCompact = compactRef.current;

      // Compact: shorter interaction ranges
      const mouseDist = isCompact ? 100 : 150;
      const maxLineDist = mobile ? 0 : (isCompact ? 80 : 120);
      const maxOpacity = mobile ? 0.5 : (isCompact ? 0.5 : 0.7);
      const maxSpeed = mobile ? 0.35 : (isCompact ? 0.45 : 0.6);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        p.x += p.vx;
        p.y += p.vy;

        p.opacity += p.opacitySpeed;
        if (p.opacity > maxOpacity) p.opacitySpeed = -Math.abs(p.opacitySpeed);
        if (p.opacity < 0.05) p.opacitySpeed = Math.abs(p.opacitySpeed);

        if (p.x < -20) p.x = canvas.width + 20;
        if (p.x > canvas.width + 20) p.x = -20;
        if (p.y < -20) p.y = canvas.height + 20;
        if (p.y > canvas.height + 20) p.y = -20;

        // Mouse interaction — desktop only
        if (!mobile) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mouseDist) {
            const force = (mouseDist - dist) / mouseDist;
            p.vx -= (dx / dist) * force * 0.02;
            p.vy -= (dy / dist) * force * 0.02;
          }
        }

        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > maxSpeed) {
          p.vx = (p.vx / speed) * maxSpeed;
          p.vy = (p.vy / speed) * maxSpeed;
        }

        // Draw particle (no glow on mobile — saves fillRate)
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.max(0.04, p.opacity).toString() + ')';
        ctx.fill();

        if (!mobile) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 2.5, 0, Math.PI * 2);
          const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2.5);
          glow.addColorStop(0, p.color + Math.max(0.01, p.opacity * 0.3).toString() + ')');
          glow.addColorStop(1, p.color + '0)');
          ctx.fillStyle = glow;
          ctx.fill();
        }
      }

      // Inter-particle lines — desktop only (O(n²) is expensive)
      if (!mobile && particles.length > 1) {
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const a = particles[i];
            const b = particles[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < maxLineDist) {
              const lineOpacity = ((maxLineDist - dist) / maxLineDist) * 0.15 * Math.min(a.opacity, b.opacity);
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.strokeStyle = 'rgba(200, 160, 80,' + Math.max(0.01, lineOpacity).toString() + ')';
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouse);
      cancelAnimationFrame(animationRef.current);
    };
  }, [initParticles]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-[2] pointer-events-none"
      aria-hidden="true"
    />
  );
}